import { Link } from "react-router-dom";
import { Mail, FolderOpen, Building2, MessagesSquare, ArrowRight } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, GhostButton } from "@/components/web/atoms";
import { Reveal, trackHomeEvent } from "@/components/web/home/primitives";

/* Section 13 — Apps & integrations, framed by outcome (not OAuth jargon).
   Every example is described only by what's actually supported. */
const GROUPS = [
  {
    key: "email", icon: Mail, title: "Email",
    copy: "Search, summarize and use communication as authorized context.",
    apps: ["Gmail", "Microsoft Outlook"],
  },
  {
    key: "files", icon: FolderOpen, title: "Files",
    copy: "Bring your files into research and knowledge.",
    apps: ["Google Drive", "OneDrive", "SharePoint*"],
  },
  {
    key: "crm", icon: Building2, title: "CRM",
    copy: "Connect customer and sales context.",
    apps: ["HubSpot", "Salesforce"],
  },
  {
    key: "communication", icon: MessagesSquare, title: "Communication",
    copy: "Bring conversations into the wider TeamNest knowledge layer.",
    apps: ["Slack", "Microsoft Teams"],
  },
];

export default function AppsSection() {
  return (
    <section
      id="apps"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-apps"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="brand" className="mb-4">Apps &amp; integrations</Eyebrow>
          <SectionTitle>Bring the tools you already use.</SectionTitle>
          <SectionSub className="mt-5">
            Connect your email, files, CRM and communication tools so TeamNest can research and remember with
            the right context.
          </SectionSub>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GROUPS.map((g, i) => (
            <Reveal key={g.key} delay={(i % 4) * 60}>
              <div className="h-full rounded-[16px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-5" data-testid={`apps-${g.key}`}>
                <span className="w-10 h-10 rounded-xl bg-[var(--w-surface2)] flex items-center justify-center mb-3">
                  <g.icon className="w-5 h-5 text-[var(--w-text)]" />
                </span>
                <div className="text-[16px] font-bold text-[var(--w-text)]">{g.title}</div>
                <p className="mt-1.5 text-[13px] leading-5 text-[var(--w-text-dim)]">{g.copy}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {g.apps.map((a) => (
                    <span key={a} className="inline-flex items-center px-2.5 py-1 rounded-full border border-[var(--w-hairline)] bg-[var(--w-bg2)] text-[11.5px] text-[var(--w-text-dim)]">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4">
          <GhostButton
            as={Link}
            to="/business"
            data-testid="apps-cta"
            onClick={() => trackHomeEvent("apps-cta", { source: "apps" })}
          >
            Explore Apps <ArrowRight className="w-4 h-4" />
          </GhostButton>
          <span className="text-[12px] text-[var(--w-text-mute)]">*Available where supported. Actions vary by connector.</span>
        </div>
      </div>
    </section>
  );
}
